import { BigDecimal, Chunk, Option, Order, pipe } from 'effect';

/**
 * Compute smart step value from a collection of attribute values
 *
 * Strategy:
 * 1. If all values are integers → step = 1
 * 2. Otherwise, find smallest non-zero difference between consecutive sorted values
 * 3. Fallback to default step if computation fails
 *
 * @example
 * computeStepFromValues([1, 2, 3, 4]) → 1 (integers)
 * computeStepFromValues([1.5, 2.0, 2.5]) → 0.5 (float increments)
 * computeStepFromValues([128, 256, 512, 1024]) → 128 (power-of-2 increments)
 */
export const computeStepFromValues = (
  values: Chunk.Chunk<number>,
  defaultStep: BigDecimal.BigDecimal = BigDecimal.fromNumberUnsafe(1)
): BigDecimal.BigDecimal => {
  if (Chunk.isEmpty(values) || Chunk.size(values) < 2) {
    return defaultStep;
  }

  // Check if all values are integers
  const allIntegers = Chunk.every(values, (val) => Number.isInteger(val));

  if (allIntegers) {
    // All integers → step = 1
    return BigDecimal.fromNumberUnsafe(1);
  }

  // Sort values and compute differences between consecutive pairs
  const sorted = Chunk.sort(values, Order.Number);

  // Find smallest non-zero difference between consecutive values
  const tailOption = Chunk.tail(sorted);

  if (Option.isNone(tailOption)) {
    return defaultStep;
  }

  const differences = pipe(
    Chunk.zipWith(sorted, tailOption.value, (prev, curr) => Math.abs(curr - prev)),
    Chunk.filter((diff) => diff > 0)
  );

  if (Chunk.isEmpty(differences)) {
    return defaultStep;
  }

  const minDiff = pipe(
    differences,
    Chunk.reduce(Number.POSITIVE_INFINITY, (acc, diff) => Math.min(acc, diff))
  );

  if (!Number.isFinite(minDiff) || minDiff === 0) {
    return defaultStep;
  }

  // Convert to BigDecimal safely
  const stepOption = BigDecimal.fromNumber(minDiff);
  return Option.getOrElse(stepOption, () => defaultStep);
};

/**
 * Detect if values form a discrete stepped sequence vs continuous range
 *
 * Returns true if:
 * - Values have consistent step increments
 * - Number of unique values is small relative to range (< 20% coverage)
 *
 * @example
 * isSteppedSequence([1, 2, 3, 4, 5]) → false (continuous)
 * isSteppedSequence([4, 8, 16, 32, 64]) → true (discrete powers of 2)
 * isSteppedSequence([128, 256, 512, 1024, 2048]) → true (discrete storage tiers)
 */
export const isSteppedSequence = (values: Chunk.Chunk<number>): boolean => {
  const size = Chunk.size(values);

  if (size < 3) {
    // Too few values to determine pattern
    return false;
  }

  const sorted = Chunk.sort(values, Order.Number);

  const minOption = Chunk.head(sorted);
  const maxOption = Chunk.last(sorted);

  if (Option.isNone(minOption) || Option.isNone(maxOption)) {
    return false;
  }

  const min = minOption.value;
  const max = maxOption.value;
  const range = max - min;

  if (range === 0) {
    return false;
  }

  // If we have many values relative to the range, it's likely continuous
  // Example: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] → continuous
  // We use a generous threshold: if more than 50% of range is covered, it's continuous
  const coverageRatio = size / range;
  if (coverageRatio > 0.5) {
    return false; // High coverage suggests continuous range
  }

  // Check if differences between consecutive values are consistent
  const tailOption = Chunk.tail(sorted);

  if (Option.isNone(tailOption)) {
    return false;
  }

  const differences = Chunk.zipWith(sorted, tailOption.value, (prev, curr) => curr - prev);

  // Allow some tolerance for floating-point precision (round to 2 decimal places)
  const uniqueDiffs = pipe(
    differences,
    Chunk.map((d) => Math.round(d * 100) / 100),
    Chunk.dedupe,
    Chunk.size
  );

  // Stepped sequence characteristics:
  // - Few unique differences (suggests discrete values, not continuous)
  // - Not too many values (< 20)
  // - Differences between values should have some variation (not uniform)
  //
  // Examples of stepped:
  // - [4, 8, 16, 32, 64] → diff=[4,8,16,32] → 4 unique diffs → stepped
  // - [128, 256, 512, 1024] → diff=[128,256,512] → 3 unique diffs → stepped
  // - [2, 4, 6, 8, 12, 16] → diff=[2,2,2,4,4] → 2 unique diffs → stepped
  //
  // Examples of continuous:
  // - [1, 2, 3, 4, 5] → diff=[1,1,1,1] → 1 unique diff + high coverage → continuous
  const hasMultipleDiffs = uniqueDiffs > 1 || (uniqueDiffs === 1 && coverageRatio < 0.5);

  return hasMultipleDiffs && size < 20;
};
